import { generateTimeSlots, type OpeningHours } from "./bookingUtils";
import {
  getExternalSlotsForDate,
  hasExternalSlots,
  type ExternalSlot,
} from "@/hooks/useClinicExternalSlots";

type ClinicSlotConfig = {
  opening_hours: unknown;
  // Whether the clinic takes bookings at times it never published. When false,
  // the partner's synced slots are the whole offer. This used to be inferred
  // from a slot count (under 100 over 30 business days meant "sparse", which
  // meant combine), but a count cannot tell "only published a few" from "only
  // accepts a few" — and getting that backwards sells an appointment the clinic
  // never agreed to. It is now a column on clinics, set deliberately.
  accepts_request_slots?: boolean;
  clinic_closed_times?: Parameters<typeof generateTimeSlots>[4];
};

/**
 * The bookable times for a date — the SINGLE source of truth shared by the date
 * picker (TimeSlotGrid) and the persisted-slot validator
 * (_validateTimeSlotsForClinic). Mirrors TimeSlotGrid's availableSlots logic
 * exactly so a slot the picker offers can never be deleted by the validator.
 *
 * Note the empty-array case: an external entry with `times: []` is a synced day
 * with no INSTANT slots — at a clinic that accepts requests it still combines
 * down to the opening-hours (request) slots, it does NOT mean "unavailable".
 * At a clinic that does not, an empty array means exactly what it says.
 */
export function getBookableTimesForDate(
  clinic: ClinicSlotConfig | undefined,
  externalSlots: ExternalSlot[] | null | undefined,
  date: Date,
  bookingMode: "external" | "request",
): string[] {
  if (!clinic) return [];

  const isWeekend = [0, 6].includes(date.getDay());
  const openingHoursSlots = generateTimeSlots(
    date,
    isWeekend,
    clinic.opening_hours as OpeningHours,
    false,
    clinic.clinic_closed_times,
  );

  const hasExt = hasExternalSlots(externalSlots);
  const useExternalSlotsOnly = hasExt && !clinic.accepts_request_slots;

  const effectiveExternalSlots =
    bookingMode === "request" ? undefined : externalSlots;
  const rawForDate = getExternalSlotsForDate(
    externalSlots,
    date,
    clinic.clinic_closed_times,
  );
  const effectiveForDate = getExternalSlotsForDate(
    effectiveExternalSlots,
    date,
    clinic.clinic_closed_times,
  );

  if (useExternalSlotsOnly) {
    // The published slots are the whole offer (an empty array genuinely means
    // no slots that day). A day with no synced row at all, past the partner's
    // window, is not open either: falling back to opening hours offered times
    // the partner never published (Eterno, 21.01.2027).
    return rawForDate ?? [];
  }
  if (rawForDate !== null) {
    // Takes requests: combine instant (external) + request (opening-hours).
    return Array.from(new Set([...rawForDate, ...openingHoursSlots])).sort();
  }
  if (effectiveForDate !== null) {
    return effectiveForDate;
  }
  return openingHoursSlots;
}
